import { useState, type FormEvent } from 'react'
import { Send } from 'lucide-react'
import { useI18n } from '@/i18n'
import { Button } from '@/components/ui/Button'
import { Input, Textarea } from '@/components/ui/Field'
import { useToast } from '@/components/ui/Toast'
import { isValidEmail } from '@/lib/format'
import { sendEnquiry } from '@/hooks/useEnquiries'
import type { Watch } from '@/types'

/**
 * "Ask about this watch" — the only conversion path on the site. There is no
 * cart: at these prices people write first, and the reply is the sale.
 *
 * Submissions land in the `enquiries` table, which the owner reads in the
 * studio inbox.
 */
export function EnquiryForm({ watch, onSent }: { watch: Watch; onSent?: () => void }) {
  const { t, tf } = useI18n()
  const { toast } = useToast()
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)

  const set = (key: keyof typeof form, value: string) => setForm((f) => ({ ...f, [key]: value }))

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const next: Record<string, string> = {}
    if (!form.name.trim()) next.name = t('enquiry.errorName')
    if (!isValidEmail(form.email)) next.email = t('enquiry.errorEmail')
    if (form.message.trim().length < 5) next.message = t('enquiry.errorMessage')
    setErrors(next)
    if (Object.keys(next).length) return

    setSending(true)
    await sendEnquiry({
      watch_id: watch.id,
      watch_title: watch.title,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      message: form.message.trim(),
    })
    setSending(false)
    setSent(true)
    toast(t('enquiry.success'))
    onSent?.()
  }

  if (sent) {
    return (
      <div
        data-upbo-type="container"
        data-upbo-label="Enquiry sent"
        className="border border-border rounded-squircle p-8 text-center flex flex-col gap-2"
      >
        <h3 className="font-display text-2xl">{t('enquiry.success')}</h3>
        <p className="text-sm text-text-muted">{tf(watch, 'title')} · {watch.reference}</p>
      </div>
    )
  }

  return (
    <form
      onSubmit={submit}
      data-upbo-id="enquiry-form"
      data-upbo-type="form"
      data-upbo-label="Enquiry form"
      className="flex flex-col gap-5"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <Input
          name="name"
          label={t('enquiry.name')}
          value={form.name}
          error={errors.name}
          onChange={(e) => set('name', e.target.value)}
        />
        <Input
          name="phone"
          label={t('enquiry.phone')}
          hint={t('common.optional')}
          value={form.phone}
          onChange={(e) => set('phone', e.target.value)}
        />
      </div>
      <Input
        name="email"
        type="email"
        label={t('enquiry.email')}
        value={form.email}
        error={errors.email}
        onChange={(e) => set('email', e.target.value)}
      />
      <Textarea
        name="message"
        label={t('enquiry.message')}
        placeholder={t('enquiry.messagePlaceholder')}
        value={form.message}
        error={errors.message}
        onChange={(e) => set('message', e.target.value)}
      />
      <Button type="submit" size="lg" loading={sending} icon={<Send size={15} strokeWidth={1.5} />}>
        {t('enquiry.submit')}
      </Button>
    </form>
  )
}
